import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for scroll-based animations
 * Detects scroll direction and triggers animations when elements enter viewport
 * Applies classes directly to DOM for better performance
 */
export const useScrollAnimation = (threshold = 0.05, rootMargin = '100px') => {
  const elementRef = useRef(null);
  const lastScrollY = useRef(window.scrollY);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Function to apply animation
    const applyAnimation = () => {
      if (hasAnimated.current) return;
      
      const currentScrollY = window.scrollY;
      const scrollDirection = currentScrollY >= lastScrollY.current ? 'down' : 'up';
      
      // Apply animation class based on scroll direction
      if (scrollDirection === 'down') {
        element.classList.add('visible-from-bottom');
      } else {
        element.classList.add('visible-from-top');
      }
      
      hasAnimated.current = true;
    };

    // Check if element is already in viewport on mount
    const checkInitialVisibility = () => {
      const rect = element.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;
      
      // If element is in viewport, animate immediately
      if (rect.top < windowHeight && rect.bottom > 0) {
        // Add a small delay for better visual effect
        setTimeout(applyAnimation, 100);
      }
    };

    // Track scroll direction and apply animation
    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          applyAnimation();
        }
      });
    };

    // Update last scroll position
    const updateScroll = () => {
      lastScrollY.current = window.scrollY;
    };

    // Intersection Observer for viewport detection
    const observer = new IntersectionObserver(handleIntersection, { 
      threshold, 
      rootMargin 
    });

    // Check initial visibility
    checkInitialVisibility();

    window.addEventListener('scroll', updateScroll, { passive: true });
    observer.observe(element);

    return () => {
      window.removeEventListener('scroll', updateScroll);
      observer.disconnect();
    };
  }, [threshold, rootMargin]);

  return elementRef;
};

/**
 * Hook for staggered animations on multiple items
 */
export const useStaggerAnimation = (itemCount, staggerDelay = 100) => {
  const [visibleItems, setVisibleItems] = useState([]);
  const containerRef = useRef(null);
  const [scrollDirection, setScrollDirection] = useState('down');
  const lastScrollY = useRef(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Track scroll direction
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY.current) {
        setScrollDirection('down');
      } else if (currentScrollY < lastScrollY.current) {
        setScrollDirection('up');
      }
      lastScrollY.current = currentScrollY;
    };

    // Intersection Observer for container
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && visibleItems.length === 0) {
          // Trigger staggered animation
          for (let i = 0; i < itemCount; i++) {
            setTimeout(() => {
              setVisibleItems((prev) => [...prev, i]);
            }, i * staggerDelay);
          }
        }
      },
      { threshold: 0.1 }
    );

    window.addEventListener('scroll', handleScroll, { passive: true });
    observer.observe(container);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, [itemCount, staggerDelay, visibleItems.length]);

  return { containerRef, visibleItems, scrollDirection };
};
